import { styled } from '@mui/material/styles';
import Section from './Section';
import { FormattedMessage } from 'react-intl';
import ListItemButton from '@mui/material/ListItemButton';
import Checkbox from '@/components/Checkbox/Checkbox';
import { mapConstantsToOptions } from '@/lib/listOptions';
import {
  ComplianceFileTypeConfig,
  FileTypesCompliance,
} from '@/constants/FileTypesCompliance';
import { useQuery } from '@tanstack/react-query';
import { fetchFilters } from '@/lib/api';

const FileTypeCompliance = ({
  value,
  onChange,
  mode = 'light',
  fileTypeConfig = FileTypesCompliance,
}: {
  value: string[];
  onChange: (values: string[]) => void;
  mode?: 'light' | 'dark';
  /**
   * The file types to offer, matching what the upload modal offers at the
   * same level (`FileTypesComplianceGame` / `FileTypesComplianceMarket`).
   * Intersected with the file types the backend reports.
   * Defaults to both sets combined.
   */
  fileTypeConfig?: ComplianceFileTypeConfig;
}) => {
  const { data } = useQuery({
    queryKey: ['filters'],
    queryFn: fetchFilters,
  });

  const supportedFileTypes = data?.compliance_documents_file_type;

  // Offer the file types of the current level, minus any the backend doesn't
  // (or no longer) supports. Until the filters are loaded, show them all.
  const fileTypeItems = mapConstantsToOptions(fileTypeConfig).filter(
    ({ id }) =>
      !supportedFileTypes ||
      supportedFileTypes.some(
        (fileType) => fileType.toLowerCase() === id,
      ),
  );

  const handleToggle = (selectedId: string | number) => () => {
    const currentIndex = value.findIndex((v) => v === selectedId);
    const newChecked = [...value];

    if (currentIndex === -1) {
      newChecked.push(selectedId as string);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    onChange(newChecked);
  };

  return (
    <Section
      title={
        <FormattedMessage id="fileType" defaultMessage="File Type" />
      }
      mode={mode}
      defaultOpen={value.length > 0}
    >
      {fileTypeItems.map(({ id, label }) => (
        <StyledButton
          key={id}
          onClick={handleToggle(id)}
          aria-label={`Toggle ${id} file type`}
        >
          <StyledCheckbox
            checked={value.includes(id as string)}
            mode={mode}
          />
          <Label>{label}</Label>
        </StyledButton>
      ))}
    </Section>
  );
};

const StyledButton = styled(ListItemButton)({
  padding: '4px 0',
});

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  padding: `0 ${theme.spacing(1)} 0 0`,
}));

const Label = styled('div')({
  fontSize: 14,
});

export default FileTypeCompliance;
